import { useQuery } from "@tanstack/react-query";
import { Star, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { NameWithAvatar } from "@/components/Avatar";

function Stars({ n }) {
  return <span className="inline-flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= n ? "fill-black text-black" : "text-zinc-300"}`} />)}</span>;
}

export function Reviews() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-reviews"], queryFn: () => api.get("/admin/reviews").then((r) => r.data) });
  if (isLoading || !data) return <p className="text-zinc-600">Loading…</p>;
  return (
    <div className="space-y-5" data-testid="admin-reviews">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="brut-card p-4"><p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Average rating</p><p className="mt-1 font-display text-3xl font-extrabold" data-testid="reviews-average">{data.average ?? "—"}<span className="text-base text-zinc-500"> / 5</span></p></div>
        <div className="brut-card p-4"><p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Reviews received</p><p className="mt-1 font-display text-3xl font-extrabold" data-testid="reviews-count">{data.count}</p></div>
        <div className="brut-card p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Distribution</p>
          <div className="mt-2 space-y-1">
            {[5, 4, 3, 2, 1].map((n) => (
              <div key={n} className="flex items-center gap-2 text-xs font-semibold"><span className="w-3">{n}</span><div className="h-2 flex-1 rounded bg-zinc-100"><div className="h-2 rounded bg-[#FACC15]" style={{ width: `${data.count ? (data.distribution[n] / data.count) * 100 : 0}%` }} /></div><span className="w-5 text-right">{data.distribution[n]}</span></div>
            ))}
          </div>
        </div>
      </div>
      <p className="flex items-center gap-2 text-sm text-zinc-600"><Lock className="h-4 w-4" /> Ratings and reviews are visible to admins only. Executives never see them.</p>
      {!data.items.length ? (
        <p className="rounded-xl border-2 border-dashed border-zinc-400 bg-white p-6 text-center text-sm text-zinc-600" data-testid="reviews-empty">No reviews yet. Customers rate from the link in their closure email.</p>
      ) : (
        <ul className="space-y-3">
          {data.items.map((r) => (
            <li key={r.id} className={`brut-card p-4 ${r.score <= 2 ? "border-[#DC2626]" : ""}`} data-testid={`review-row-${r.ticket_id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-lg font-bold">{r.ticket_id}</span>
                <Stars n={r.score} />
                <span className="text-sm font-semibold">{r.score}/5</span>
                <span className="pill border-black bg-[#FACC15]" data-testid={`review-exec-${r.ticket_id}`}><NameWithAvatar id={r.executive_id} name={r.executive} size={18}>Handled by {r.executive}</NameWithAvatar></span>
                <span className="ml-auto text-xs text-zinc-500">{fmtDate(r.rated_at)}</span>
              </div>
              <p className="mt-1 text-xs text-zinc-500">{r.customer} · {r.email} · {r.product} — {r.query}</p>
              <p className="mt-2 whitespace-pre-wrap text-sm" data-testid={`review-text-${r.ticket_id}`}>{r.review || <span className="italic text-zinc-400">No written review</span>}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
